// Bid Tracker value, deadline and "gone quiet" helpers.
//
// A bid's value used to be free text ("£1.0m", "£2.1m inc VAT", "Framework",
// "Major"). Migration 0327 adds a numeric value_gbp with three qualifiers: ex
// or inc VAT (value_vat), contract value or framework ceiling (value_basis),
// estimated or verified (value_confidence). The legacy est_value text stays on
// the row until someone confirms a figure, so the tracker needs to read both,
// and to turn the old text into a suggestion the person can accept.
//
// Pure functions only: nothing here knows about React, the API or the theme, so
// tests/bid-value.test.mjs can run them directly.

/** "£1.0m" or "250k" or "£1,200,000" as pounds, or null. */
// A money token: an optional £, digits with commas, an optional decimal and an
// optional scale (k, m, mil, million, b, bn, billion).
const MONEY = /£?(\d[\d,]*(?:\.\d+)?)\s*(k|m|mil|million|b|bn|billion)?(?![\w.])/i;
const SCALE = { k: 1e3, m: 1e6, mil: 1e6, million: 1e6, b: 1e9, bn: 1e9, billion: 1e9 };
function moneyToken(s) {
  const m = String(s).match(/^£?([\d,]+(?:\.\d+)?)(k|m|mil|million|b|bn|billion)?$/i);
  if (!m) return null;
  const n = parseFloat(m[1].replace(/,/g, ''));
  if (!isFinite(n)) return null;
  return n * (m[2] ? SCALE[m[2].toLowerCase()] : 1);
}

// Legacy est_value text read strictly: the whole string has to be a figure. A
// column total can only cover the ones that actually are, so "Framework",
// "Major" and "—" return null and count as unpriced rather than as zero, which
// would understate the column and say so nowhere. "£2.1m inc VAT" is also null
// here on purpose: whether that is a contract value or a ceiling is for a
// person to confirm (see suggestValueFromLegacy), not for a total to assume.
export function parseEstValue(v) {
  if (v == null) return null;
  return moneyToken(String(v).trim().replace(/\s+/g, ''));
}

// Rounded, because a bid value is approximate by nature: "£220k", "£2.0m",
// "£1,500". Anything that is not a number formats as nothing rather than £NaN.
export function formatValueGbp(n) {
  const v = typeof n === 'string' ? Number(n) : n;
  if (v == null || typeof v !== 'number' || !isFinite(v)) return '';
  if (v >= 1e6) return '£' + (v / 1e6).toFixed(1) + 'm';
  if (v >= 1e4) return '£' + Math.round(v / 1e3) + 'k';
  return '£' + Math.round(v).toLocaleString('en-GB');
}

// The small badges shown beside a formatted value. Only the qualifiers that
// need saying: an ex VAT, verified contract value is the plain case.
export function valueBadges(bid) {
  if (!bid || bid.value_gbp == null) return [];
  const out = [];
  if (bid.value_vat === 'inc') out.push('inc VAT');
  if (bid.value_basis === 'framework_ceiling') out.push('Ceiling');
  if (bid.value_confidence === 'estimated') out.push('Est.');
  return out;
}

// The figure a total can use: ex VAT, contract values only. A framework
// ceiling is the most a framework might ever spend, not what this bid is
// worth, so it has no place in a pipeline total.
export function exVatValue(bid) {
  if (!bid || bid.value_gbp == null) return null;
  const v = Number(bid.value_gbp);
  if (!isFinite(v)) return null;
  if (bid.value_basis === 'framework_ceiling') return null;
  return bid.value_vat === 'inc' ? v / 1.2 : v;
}

// Reads a legacy est_value text into the four new fields, as a suggestion. The
// first money token in the text is the figure; the rest of the text decides
// the qualifiers. Confidence is always 'estimated': a figure nobody has
// confirmed against the tender documents is an estimate whatever the text
// says about itself. Returns null when there is no figure to suggest.
export function suggestValueFromLegacy(text) {
  if (text == null) return null;
  const s = String(text).trim();
  if (!s) return null;
  const EX = /\b(?:ex|exc|excl|excluding)\.?\s*vat\b/i;
  const INC = /\b(?:inc|incl|including)\.?\s*vat\b/i;
  // Notes often quote both figures ("£2.1m inc VAT (approx £1.75m ex VAT)").
  // Reporting works ex VAT, so the figure sitting next to "ex VAT" wins, then
  // the one next to "inc VAT", then the first figure in the text.
  const beside = (re) => {
    const m = s.match(new RegExp('(' + MONEY.source + ')' + String.raw`\s*(?:\([^)]*\)\s*)?` + re.source, 'i'));
    return m ? moneyToken(m[1].replace(/\s+/g, '')) : null;
  };
  const exFigure = beside(EX);
  const incFigure = exFigure == null ? beside(INC) : null;
  const first = s.match(MONEY);
  const value_gbp = exFigure ?? incFigure ?? (first ? moneyToken(first[0].replace(/\s+/g, '')) : null);
  if (value_gbp == null) return null;
  const value_vat = exFigure != null ? 'ex' : (incFigure != null || (!EX.test(s) && /\bvat\b/i.test(s))) ? 'inc' : 'ex';
  return {
    value_gbp,
    value_vat,
    value_basis: /framework/i.test(s) ? 'framework_ceiling' : 'contract',
    value_confidence: 'estimated',
  };
}

// Column totals: value_gbp where it is a contract value (inc VAT brought back
// to ex VAT), legacy text where there is no figure yet, framework ceilings
// counted rather than added, and anything else counted as unpriced.
export function valueTotals(bids) {
  let sum = 0, priced = 0, ceilings = 0, unpriced = 0;
  for (const b of bids || []) {
    if (b.value_gbp != null && b.value_basis === 'framework_ceiling') { ceilings++; continue; }
    const v = exVatValue(b) ?? parseEstValue(b.est_value);
    if (v == null) { unpriced++; continue; }
    sum += v; priced++;
  }
  return { value: priced ? sum : null, ceilings, unpriced };
}

/* ── Dates ───────────────────────────────────────────────────────────────── */

// A date-only UTC value from an ISO date, an ISO datetime or a SQLite
// "YYYY-MM-DD HH:MM:SS"; null for anything else.
function utcDay(v) {
  if (!v) return null;
  const s = String(v).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(s + 'T00:00:00Z');
  return isNaN(d) ? null : d;
}

// A Responded bid that nobody has moved or checked on for more than 30 days.
// Both dates are optional; the later one counts, and with neither there is no
// evidence either way, so the answer is no.
export function goneQuiet(bid, today = new Date()) {
  if (!bid || !['Responded', 'Awaiting decision'].includes(bid.status)) return false;
  const marks = [utcDay(bid.status_changed_at), utcDay(bid.last_checked_at)].filter(Boolean);
  if (!marks.length) return false;
  const latest = Math.max(...marks.map(d => +d));
  const now = utcDay(today instanceof Date ? today.toISOString() : today);
  if (!now) return false;
  return (now - latest) / 86400000 > 30;
}

// Sort by deadline date then time. Undated bids go last whichever way the
// dated ones are ordered, and ties fall back to id so the order is stable
// rather than however the rows happened to arrive.
export function deadlineSort(a, b, dir = 'asc') {
  const ka = deadlineKey(a), kb = deadlineKey(b);
  if (ka && kb && ka !== kb) return dir === 'desc' ? (ka < kb ? 1 : -1) : (ka < kb ? -1 : 1);
  if (ka && !kb) return -1;
  if (!ka && kb) return 1;
  return (a.id || 0) - (b.id || 0);
}
function deadlineKey(b) {
  const d = b && b.deadline ? String(b.deadline).slice(0, 10) : '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  return d + 'T' + (b.deadline_time || '00:00');
}

// "10 Sep 2026, 12:00", or the date alone without a time. No deadline is a
// dash; text that is not a date (an old key_date) is passed through as it is.
// The month names are a fixed table rather than toLocaleDateString, whose
// en-GB data abbreviates September as "Sept" in some ICU versions and "Sep" in
// others, so the same deadline would otherwise read differently by browser.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function fmtDeadlineWithTime(deadline, deadline_time) {
  if (!deadline) return '—';
  const d = utcDay(deadline);
  if (!d) return String(deadline);
  const date = `${String(d.getUTCDate()).padStart(2, '0')} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  return deadline_time ? `${date}, ${deadline_time}` : date;
}
